/**
 * Hook dữ liệu dùng chung giữa các nhóm màn của Trợ lý AI. View chỉ gọi hook, không gọi repository (CLAUDE.md).
 *
 * Hook riêng của từng nhóm màn nằm ở `hooks/use<Nhóm>.ts` và dùng chung `KHOA` dưới đây để vô hiệu hoá chéo được: lưu
 * một nguồn ở Kết nối nguồn phải làm mới cả lưới nguồn ở Kho dữ liệu lẫn ô chọn nguồn ở Hỏi đáp.
 */
import { useQuery } from '@tanstack/react-query';

import type { ApiError } from '../../../lib/errors';
import { khoTroLy } from '../../../lib/repositories/tro-ly-ai.repository';

/** Khoá cache: mọi thứ của module nằm dưới `['tro-ly-ai']`, vô hiệu hoá theo nhánh. */
export const KHOA = {
  goc: ['tro-ly-ai'] as const,
  nguon: ['tro-ly-ai', 'nguon'] as const,
  nguonChiTiet: (id: string) => ['tro-ly-ai', 'nguon', id] as const,
  trangThai: (id: string) => ['tro-ly-ai', 'nguon', id, 'trang-thai'] as const,
  bot: ['tro-ly-ai', 'bot'] as const,
  botQuanTri: ['tro-ly-ai', 'bot', 'quan-tri'] as const,
  botDungDuoc: ['tro-ly-ai', 'bot', 'dung-duoc'] as const,
  botChiTiet: (id: string) => ['tro-ly-ai', 'bot', 'chi-tiet', id] as const,
  sucKhoe: ['tro-ly-ai', 'suc-khoe'] as const,
  vaiTro: ['tro-ly-ai', 'vai-tro'] as const,
  boTriThuc: ['tro-ly-ai', 'bo-tri-thuc'] as const,
  khuon: (sourceId: string) => ['tro-ly-ai', 'khuon', sourceId] as const,
  quyen: ['tro-ly-ai', 'quyen'] as const,
  nhatKy: ['tro-ly-ai', 'nhat-ky'] as const,
  suDung: (ngay: number) => ['tro-ly-ai', 'su-dung', ngay] as const,
  hoiThoai: ['tro-ly-ai', 'hoi-thoai'] as const,
};

/** 4xx là câu trả lời của máy chủ, không phải trục trặc: thử lại chỉ bắt người dùng chờ thêm. */
export function thuLai(soLanDaThu: number, err: unknown): boolean {
  const status = (err as ApiError | undefined)?.status;
  if (status !== undefined && status >= 400 && status < 500) return false;
  return soLanDaThu < 1;
}

/** Mọi nguồn của tenant — lưới nguồn, ô chọn nguồn, ma trận quyền đều đọc chung. */
export function useDanhSachNguon(bat = true) {
  return useQuery({
    queryKey: KHOA.nguon,
    queryFn: khoTroLy.danhSachNguon,
    staleTime: 30_000,
    retry: thuLai,
    enabled: bat,
  });
}

/** Bot người đang đăng nhập chat được — lưới "Trò chuyện với bot". */
export function useBotDungDuoc(bat = true) {
  return useQuery({
    queryKey: KHOA.botDungDuoc,
    queryFn: khoTroLy.botDungDuoc,
    staleTime: 60_000,
    retry: thuLai,
    enabled: bat,
  });
}

/**
 * Danh mục mô hình và giọng của tầng hỏi–đáp. Đổi khi đội vận hành cấu hình khoá — hiếm, nên giữ lâu. Lỗi ở đây không
 * được chặn màn: không có danh mục thì ô chọn mô hình rơi về mặc định máy chủ và nút loa ẩn.
 */
export function useSucKhoeTroLy() {
  return useQuery({
    queryKey: KHOA.sucKhoe,
    queryFn: khoTroLy.sucKhoeTroLy,
    staleTime: 5 * 60_000,
    retry: thuLai,
  });
}

/** Vai trò của tổ chức (từ API vai trò của platform) — cột/dòng của các ma trận quyền và vai của khung nhúng. */
export function useVaiTroToChuc(bat = true) {
  return useQuery({
    queryKey: KHOA.vaiTro,
    queryFn: khoTroLy.vaiTroToChuc,
    staleTime: 5 * 60_000,
    retry: thuLai,
    enabled: bat,
  });
}

/** Bộ tri thức gắn được vào bot/nguồn (`{id: collection_id, ten}`) — từ module tri thức. */
export function useBoTriThuc(bat = true) {
  return useQuery({
    queryKey: KHOA.boTriThuc,
    queryFn: khoTroLy.boTriThuc,
    staleTime: 5 * 60_000,
    retry: thuLai,
    enabled: bat,
  });
}

/**
 * Tên vai để HIỆN cho người đọc, tách khỏi mã vai trong nhật ký (`query_runs.actor` ghi mã). Tra trong danh sách vai
 * của tổ chức; không thấy thì hạ mã xuống chữ thường, viết hoa chữ đầu — không bịa tên.
 */
export function tenVaiTuDanhSach(ma: string, ds: { ma: string; ten: string }[] | undefined): string {
  const k = (ma || '').trim();
  if (!k) return '—';
  const co = ds?.find((v) => v.ma.toUpperCase() === k.toUpperCase());
  if (co && co.ten !== co.ma) return co.ten;
  const chu = k.replace(/_/g, ' ').toLowerCase();
  return chu.charAt(0).toUpperCase() + chu.slice(1);
}

/** Một giọng MÁY CHỦ đọc được. */
export interface Giong {
  ma: string;
  nhan: string;
  ngonNgu: string;
}

/**
 * Giọng máy chủ đọc được (từ `sucKhoeTroLy`). Rỗng ⇒ ẩn hẳn nút loa thay vì hiện một ô chọn không có mục nào. Lỗi ở đây
 * KHÔNG hiện ra: không đọc được danh sách giọng thì ẩn tính năng phụ, không báo lỗi cho thứ người dùng chưa hỏi tới.
 */
export function useDanhSachGiong(): Giong[] {
  const { data } = useSucKhoeTroLy();
  return (data?.giong ?? []).map((g) => ({ ma: g.ma, nhan: g.nhan, ngonNgu: g.ngon_ngu }));
}
